/**
 * /oauth/consent — OAuth 2.1 consent screen (MCP grant flow).
 *
 * Public route (child of the root, NOT the authed layout): better-auth 302s
 * the browser here from the authorize endpoint once a user is signed in,
 * carrying ?consent_code=…&client_id=…&scope=… . The consent-details
 * endpoint is session-only, so a logged-out visitor is bounced to /login
 * with the grant params preserved — login's resume branch sends them right
 * back here.
 *
 * Everything user-visible comes from GET /api/oauth/consent-details (the URL
 * params only carry the code); never render client/scope data from the URL.
 * Approve/deny POSTs /api/auth/oauth2/consent; on approve the response
 * carries the client's redirect URI, followed with a full navigation because
 * the end target is external (claude.ai or a localhost callback).
 */
import { useSearch } from '@tanstack/react-router';
import { AlertCircle, AlertTriangle, Check, Loader2, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AuthShell } from '@/components/auth/auth-shell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, type OAuthConsentDetails, oauthConsentApi } from '@/lib/api';
import { authClient } from '@/lib/auth-client';
import { hardNavigate, loginUrlWithGrant } from '@/lib/oauth';

/** Plain-language scope labels; unknown scopes render raw. */
const SCOPE_LABELS: Record<string, string> = {
  'brain:read': 'Read and search your brain',
  'brain:write': 'Add and update memories',
  offline_access: 'Stay connected without re-approving',
};

/**
 * Client-supplied URIs (client_uri, logo) are only ever used as anchor/img
 * targets for https: — plus http://localhost/127.0.0.1 in dev. Anything else
 * (javascript:, data:, …) renders as plain text / falls back to the icon.
 * Defense-in-depth: the server already nulls non-https values at the API.
 */
function isNavigableUri(uri: string): boolean {
  try {
    const url = new URL(uri);
    if (url.protocol === 'https:') return true;
    return (
      url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1')
    );
  } catch {
    return false;
  }
}

type PageState =
  | { kind: 'loading' }
  | { kind: 'ready'; details: OAuthConsentDetails }
  | { kind: 'expired' }
  | { kind: 'error' }
  | { kind: 'denied' };

export function ConsentPage() {
  const search = useSearch({ strict: false }) as {
    consent_code?: string;
    client_id?: string;
    scope?: string;
  };
  const consentCode = search.consent_code ?? '';
  const clientId = search.client_id ?? '';
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const [submitting, setSubmitting] = useState<'approve' | 'deny' | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // The grant needs a signed-in user: bounce to login with the params login's
  // resume branch needs to come straight back here.
  useEffect(() => {
    if (sessionPending || session || !consentCode || !clientId) return;
    hardNavigate(loginUrlWithGrant(consentCode, clientId));
  }, [sessionPending, session, consentCode, clientId]);

  // Authoritative display data — fetched for every visit, never trusted from
  // the URL.
  useEffect(() => {
    if (sessionPending || !session || !consentCode) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    oauthConsentApi
      .details(consentCode)
      .then((details) => {
        if (!cancelled) setState({ kind: 'ready', details });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // The server answers a uniform 404 for unknown, expired, AND foreign
        // codes (plan R5 — a foreign code is indistinguishable from unknown).
        if (err instanceof ApiError && err.status === 404) setState({ kind: 'expired' });
        else setState({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [sessionPending, session, consentCode]);

  async function decide(accept: boolean) {
    setSubmitError(null);
    setSubmitting(accept ? 'approve' : 'deny');
    try {
      const res = await oauthConsentApi.decide(consentCode, accept);
      if (!accept) {
        setState({ kind: 'denied' });
        return;
      }
      const target = res.redirectURI ?? res.redirect_uri;
      if (target) {
        // External target (the client's redirect_uri) — full navigation.
        hardNavigate(target);
      } else {
        setSubmitError('Approved, but the server returned no redirect. Return to your AI app.');
        setSubmitting(null);
      }
    } catch (err) {
      // The consent POST 401s on a bad/expired/already-consumed consent_code.
      setSubmitError(
        err instanceof ApiError && (err.status === 401 || err.status === 404)
          ? 'This sign-in link expired. Restart the connection from your AI app.'
          : 'Something went wrong. Please try again.',
      );
      setSubmitting(null);
    }
  }

  // No code at all → the link is malformed; say so immediately (a logged-out
  // visit without a code must not hang on the session bounce or skeleton).
  if (!consentCode) {
    return (
      <div className="flex min-h-screen flex-col">
        <AuthShell>
          <div className="text-center">
            <h1 className="text-lg font-semibold">Invalid sign-in link</h1>
            <p className="mt-1.5 text-sm text-faint">
              This link is missing its sign-in code. Restart the connection from your AI app.
            </p>
          </div>
        </AuthShell>
      </div>
    );
  }

  if (sessionPending || state.kind === 'loading') {
    return (
      <div className="flex min-h-screen flex-col">
        <AuthShell>
          <div className="space-y-3">
            <Skeleton className="mx-auto h-12 w-12 rounded-xl" />
            <Skeleton className="mx-auto h-5 w-48" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-[46px] w-full" />
          </div>
        </AuthShell>
      </div>
    );
  }

  if (!session) {
    // Redirect to /login is in flight from the effect above.
    return null;
  }

  if (state.kind === 'expired') {
    return (
      <div className="flex min-h-screen flex-col">
        <AuthShell>
          <div className="text-center">
            <h1 className="text-lg font-semibold">Link expired</h1>
            <p className="mt-1.5 text-sm text-faint">
              This sign-in link expired or belongs to a different account — restart the connection
              from your AI app, or sign in as a different user.
            </p>
          </div>
        </AuthShell>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="flex min-h-screen flex-col">
        <AuthShell>
          <div className="text-center">
            <h1 className="text-lg font-semibold">Something went wrong</h1>
            <p className="mt-1.5 text-sm text-faint">
              We couldn&apos;t load this sign-in request. Reload the page, or restart the connection
              from your AI app.
            </p>
          </div>
        </AuthShell>
      </div>
    );
  }

  if (state.kind === 'denied') {
    return (
      <div className="flex min-h-screen flex-col">
        <AuthShell>
          <div className="text-center">
            <h1 className="text-lg font-semibold">Connection cancelled</h1>
            <p className="mt-1.5 text-sm text-faint">You can close this tab.</p>
          </div>
        </AuthShell>
      </div>
    );
  }

  const { details } = state;

  return (
    <div className="flex min-h-screen flex-col">
      <AuthShell>
        <div className="mb-6 text-center">
          {details.logoUri && isNavigableUri(details.logoUri) ? (
            <img src={details.logoUri} alt="" className="mx-auto mb-3 h-12 w-12 rounded-xl" />
          ) : (
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
              <ShieldCheck className="h-6 w-6 text-foreground" />
            </div>
          )}
          <h1 className="text-lg font-semibold">Connect {details.clientName} to Brain?</h1>
          {details.clientUri &&
            (isNavigableUri(details.clientUri) ? (
              <a
                href={details.clientUri}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-block text-sm text-faint hover:text-foreground hover:underline"
              >
                {details.clientUri}
              </a>
            ) : (
              <p className="mt-1 text-sm text-faint">{details.clientUri}</p>
            ))}
          {/* Which workspace this connector was added for. With per-org MCP URLs
              a user in several orgs approves several connectors, and this line is
              the only thing that tells the screens apart. Server-resolved from
              the same answer the grant is bound to — never from the URL.

              The second sentence is not hedging, and please do not "tidy" it
              away. An access token carries `aud: client_id` and no resource
              claim (better-auth mcp/index.mjs), so nothing in the credential
              records which URL it was minted for — `resolveScopedOAuthPrincipal`
              checks membership and identity, and any workspace the user belongs
              to is therefore reachable with it. Naming one workspace and
              stopping there would promise a narrower grant than we actually
              issue. Deciding to keep that capability is what makes stating it
              mandatory (security review, finding 4). */}
          {details.workspaceName && (
            <>
              <p className="mt-2 text-sm text-faint">
                Added for{' '}
                <span className="font-semibold text-foreground">{details.workspaceName}</span>
              </p>
              <p className="mt-1 text-sm text-faint">
                This connector will also be able to read the other workspaces you belong to.
              </p>
            </>
          )}
        </div>

        <p className="text-sm font-medium">This will allow {details.clientName} to:</p>
        <ul className="mt-2 space-y-2">
          {details.scopes.map((scope) => (
            <li key={scope} className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              <span>{SCOPE_LABELS[scope] ?? scope}</span>
            </li>
          ))}
        </ul>

        {/* Claude connector requirement: the redirect destination is shown prominently. */}
        {details.redirectUriHost && (
          <div className="mt-5 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
            After you approve, your sign-in will be sent to{' '}
            <span className="font-semibold text-foreground">{details.redirectUriHost}</span>.
          </div>
        )}

        {details.isLoopbackOnly && (
          <Alert variant="warning" className="mt-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This application only receives sign-ins on this computer (localhost). Only approve if
              you started this connection yourself.
            </AlertDescription>
          </Alert>
        )}

        {submitError && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}

        <div className="mt-6 space-y-2">
          <Button
            size="cta"
            className="w-full"
            disabled={submitting !== null}
            onClick={() => void decide(true)}
          >
            {submitting === 'approve' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Approve'}
          </Button>
          <Button
            variant="outline"
            size="cta"
            className="w-full"
            disabled={submitting !== null}
            onClick={() => void decide(false)}
          >
            {submitting === 'deny' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Deny'}
          </Button>
        </div>
      </AuthShell>
    </div>
  );
}
